import type { GoogleGetStartedProfile } from '../types/auth';

/**
 * A Google account with no workspace is a sign-up, not a failed login.
 * `/auth/google` answers 403 `NO_TENANT` with `data.next_step: 'get_started'`
 * and the verified profile; the login button then hands the Google credential
 * to the sign-up wizard, which asks for the organization and legal details and
 * posts everything to `/auth/google/signup`.
 */

export interface GoogleGetStartedHandoff {
  /** Raw Google id_token. The server re-verifies it; the wizard only forwards it. */
  credential: string;
  profile: GoogleGetStartedProfile;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * The profile out of a "get started" response, or `null` for any other failure
 * (including a plain `NO_TENANT`, which is how a deactivated member is told no).
 */
export const parseGoogleGetStarted = (err: unknown): GoogleGetStartedProfile | null => {
  if (!isRecord(err) || !isRecord(err.response)) return null;
  const { status, data: envelope } = err.response;
  if (status !== 403 || !isRecord(envelope) || envelope.error !== 'NO_TENANT') return null;

  const hint = envelope.data;
  if (!isRecord(hint) || hint.next_step !== 'get_started' || !isRecord(hint.profile)) return null;

  const email = text(hint.profile.email);
  if (!email) return null;

  return {
    email,
    firstName: text(hint.profile.first_name),
    lastName: text(hint.profile.last_name),
  };
};

// Held in module memory on purpose: a bearer credential has no business in
// web storage or in history state. A reload loses it and the visitor simply
// starts the sign-up again.
let pending: GoogleGetStartedHandoff | null = null;

export const stashGoogleGetStarted = (handoff: GoogleGetStartedHandoff): void => {
  pending = handoff;
};

/** Non-destructive read, so a StrictMode double render sees the same value. */
export const peekGoogleGetStarted = (): GoogleGetStartedHandoff | null => pending;

export const clearGoogleGetStarted = (): void => {
  pending = null;
};
